import { useQuery, useMutation, keepPreviousData } from '@tanstack/react-query';
import type {
  InventoryVehiclesParams,
  InventoryVehiclesResponse,
  InventoryFilters,
  WhoAmIResponse,
  UserLocation,
  PrCodesResponse,
  CheckoutDetailRequest,
  CheckoutDetailResponse,
} from './dto.inventory';
import { transformCheckoutDetail, type TransformedCheckoutDetail } from './transformers.checkout';
import { LUPP_QUERY_KEYS } from '../constants.lupp';

/*
|--------------------------------------------------------------------------
| API BASE URLS
|--------------------------------------------------------------------------
*/

const LOCATION_API_URL = 'https://lucidmotors.com/portalapi/whoami';
const CPQ_BASE_URL = 'https://cpq.lucidmotors.com/cpq/configurator';

/**
 * Builds the inventory base URL with the given locale
 * @param locale - Locale string (e.g., 'en-us', 'de-de')
 * @returns Full inventory base URL with locale
 */
const getInventoryBaseUrl = (locale: string): string => {
  // Convert locale format from 'en-US' to 'en_us' for the API
  const apiLocale = locale.toLowerCase().replace('-', '_');

  const baseUrl = import.meta.env.VITE_INVENTORY_BASE_URL;
  if (baseUrl) {
    // If env variable is set, append locale and path
    return `${baseUrl}/${apiLocale}/store/inventory`;
  }

  // Fallback to full URL construction
  return `https://sales.stage.lucidgroup.com/buynow/api/v4/${apiLocale}/store/inventory`;
};

/*
|--------------------------------------------------------------------------
| GET USER LOCATION
|--------------------------------------------------------------------------
*/

// Default location fallback: 7373 Gateway Blvd, Newark, CA 94560, United States
const DEFAULT_LOCATION: UserLocation = {
  lat: 37.5259,
  long: -122.0232,
  region: 'CA',
  postcode: '94560',
  country: 'US',
};

/**
 * Fetches the user's location based on their IP address
 * Falls back to Newark, CA location if the API fails or returns empty values
 * @param enabled - Whether the query should be enabled
 * @returns React Query result with user location data
 *
 * @example
 * const { data: location } = useGetLocation();
 * // location.lat, location.long, location.region
 */
export const useGetLocation = (enabled = true) => {
  return useQuery({
    queryKey: [LUPP_QUERY_KEYS.USER_LOCATION],
    queryFn: async (): Promise<UserLocation> => {
      try {
        const response = await fetch(LOCATION_API_URL, {
          method: 'GET',
          headers: {
            accept: 'application/json',
          },
        });

        if (!response.ok) {
          // Return default location on error
          return DEFAULT_LOCATION;
        }

        const data: WhoAmIResponse = await response.json();

        // Check if the response has valid data - fallback to defaults for empty values
        const lat = data.msg?.Lat ? parseFloat(data.msg.Lat) : DEFAULT_LOCATION.lat;
        const long = data.msg?.Long ? parseFloat(data.msg.Long) : DEFAULT_LOCATION.long;
        const region = data.msg?.Region || DEFAULT_LOCATION.region;
        const postcode = data.msg?.Postcode || DEFAULT_LOCATION.postcode;
        const country = data.msg?.Country || DEFAULT_LOCATION.country;

        // If lat/long are NaN or 0, use defaults
        return {
          lat: isNaN(lat) || lat === 0 ? DEFAULT_LOCATION.lat : lat,
          long: isNaN(long) || long === 0 ? DEFAULT_LOCATION.long : long,
          region,
          postcode,
          country,
        };
      } catch {
        // Return default location on any error (network, parsing, etc.)
        return DEFAULT_LOCATION;
      }
    },
    enabled,
    staleTime: 1000 * 60 * 30, // 30 minutes - location doesn't change often
    gcTime: 1000 * 60 * 60, // 1 hour cache
  });
};

/*
|--------------------------------------------------------------------------
| GET PR CODES (CPQ Configurator)
|--------------------------------------------------------------------------
*/

import { transformPrCodes, type ParsedFilterOptions } from './transformers.inventory';

export type ModelType = 'air' | 'gravity';

/**
 * Fetches prCodes (product codes) for a specific model from the CPQ configurator
 * @param model - The model type ('air' or 'gravity')
 * @param year - The model year (default: '2026')
 * @param locale - The locale for the API call (default: 'en-us')
 * @param enabled - Whether the query should be enabled
 * @returns React Query result with parsed filter options
 *
 * @example
 * const { data: prCodes } = useGetPrCodes('air', '2026', 'en-us');
 * // prCodes.exteriorColor, prCodes.wheels, etc.
 */
export const useGetPrCodes = (
  model: ModelType,
  year = '2026',
  locale = 'en-us',
  enabled = true
) => {
  // Air uses v2, Gravity uses v1
  const apiVersion = model === 'air' ? 'v2' : 'v1';
  // Convert locale format from 'en-US' to 'en-us' for the API
  const apiLocale = locale.toLowerCase();

  return useQuery({
    queryKey: [LUPP_QUERY_KEYS.PR_CODES, model, year, locale],
    queryFn: async (): Promise<ParsedFilterOptions> => {
      const response = await fetch(
        `${CPQ_BASE_URL}/${apiVersion}/${apiLocale}/${model}/${year}/prCodes`,
        {
          method: 'GET',
          headers: {
            accept: '*/*',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch prCodes: ${response.statusText}`);
      }

      const data: PrCodesResponse = await response.json();
      return transformPrCodes(data);
    },
    enabled,
    staleTime: 1000 * 60 * 60, // 1 hour - prCodes don't change often
    gcTime: 1000 * 60 * 60 * 24, // 24 hour cache
  });
};

/*
|--------------------------------------------------------------------------
| HELPER FUNCTIONS
|--------------------------------------------------------------------------
*/

/**
 * Converts InventoryFilters to API params
 * API Parameter Mapping:
 * - type: condition (new, demo, pre-owned)
 * - modelVariant: trim (PURE, AT, GT)
 * - color: exteriorColor (L102, L901, L806, etc.)
 * - interior: interiorTheme (INT21, INT18, INT12, etc.)
 * - wheels: wheels (WH00, WH01, etc.)
 * - appearance: appearance (EXT01M, EXT02G, etc.)
 * - driverAssistance: driverAssistance (AD00, AD01)
 * - soundSystem: soundSystem (AU00, AU01)
 * - seatingOptions: seating (12W2, etc.)
 * - vehiclePackage: packages (CCP01, CCP02, CCP03)
 * - availability: availability (AVAILABLE_NOW, AVAILABLE_IN_2_WEEKS)
 * - distance: searchWithin
 */
const filtersToParams = (filters: InventoryFilters): Record<string, string | undefined> => {
  const params: Record<string, string | undefined> = {};

  if (filters.condition) {
    params.type = filters.condition;
  }

  if (filters.trim && filters.trim.length > 0) {
    params.modelVariant = filters.trim.join(',');
  }

  if (filters.searchWithin) {
    params.distance = filters.searchWithin;
  }

  if (filters.availability && filters.availability.length > 0) {
    params.availability = filters.availability.join(',');
  }

  if (filters.exteriorColor && filters.exteriorColor.length > 0) {
    params.color = filters.exteriorColor.join(',');
  }

  if (filters.interiorTheme && filters.interiorTheme.length > 0) {
    params.interior = filters.interiorTheme.join(',');
  }

  if (filters.wheels && filters.wheels.length > 0) {
    params.wheels = filters.wheels.join(',');
  }

  if (filters.appearance && filters.appearance.length > 0) {
    params.appearance = filters.appearance.join(',');
  }

  if (filters.driverAssistance && filters.driverAssistance.length > 0) {
    params.driverAssistance = filters.driverAssistance.join(',');
  }

  if (filters.soundSystem && filters.soundSystem.length > 0) {
    params.soundSystem = filters.soundSystem.join(',');
  }

  if (filters.seating && filters.seating.length > 0) {
    params.seatingOptions = filters.seating.join(',');
  }

  if (filters.packages && filters.packages.length > 0) {
    params.vehiclePackage = filters.packages.join(',');
  }

  return params;
};

/*
|--------------------------------------------------------------------------
| GET INVENTORY VEHICLES
|--------------------------------------------------------------------------
*/

export type UseGetInventoryVehiclesParams = InventoryVehiclesParams & {
  filters?: InventoryFilters;
  locale?: string;
};

/**
 * Fetches available inventory vehicles from the Lucid Motors inventory API
 * @param params - Query parameters for filtering vehicles
 * @returns React Query result with inventory vehicles data
 *
 * @example
 * const { data, isLoading } = useGetInventoryVehicles({
 *   lat: 32.89460,
 *   long: -111.74930,
 *   locale: 'en-us',
 *   filters: { condition: 'new', trim: ['PURE', 'AT'] },
 * });
 */
export const useGetInventoryVehicles = (params: UseGetInventoryVehiclesParams) => {
  const {
    long,
    lat,
    type = 'new',
    modelVariant = 'PURE,AT,GT',
    modelYear = '2025,2026',
    sortType = 'distance',
    distance = 10000,
    zipState = 'AZ',
    requestedVehiclesNum = 72,
    enabled = true,
    filters = {},
    locale = 'en-us',
  } = params;

  // Get filter-based params
  const filterParams = filtersToParams(filters);

  // Determine the model from filters (Air or Gravity)
  // Air uses /vehicles endpoint, Gravity uses /vehicles/gravity endpoint
  const selectedModel = filters.model?.[0] || 'AIR';
  const isGravity = selectedModel === 'GRAVITY';

  return useQuery({
    queryKey: [
      LUPP_QUERY_KEYS.INVENTORY_VEHICLES,
      long,
      lat,
      modelYear,
      sortType,
      zipState,
      selectedModel,
      // Include filters directly for better change detection
      filters,
    ],
    queryFn: async (): Promise<InventoryVehiclesResponse> => {
      // Build base params - only include required params
      const baseParams: Record<string, string> = {
        long: long.toString(),
        lat: lat.toString(),
        type: filterParams.type || type,
        sortType,
        distance: filterParams.distance || distance.toString(),
        zipState,
        requestedVehiclesNum: requestedVehiclesNum.toString(),
      };

      // Only add availability if explicitly set in filters
      if (filterParams.availability) {
        baseParams.availability = filterParams.availability;
      }

      // Add modelVariant (trim) if specified in filters
      if (filterParams.modelVariant) {
        baseParams.modelVariant = filterParams.modelVariant;
      } else if (!isGravity) {
        // Default modelVariant only for Air
        baseParams.modelVariant = modelVariant;
      }

      // Add modelYear only for Air
      if (!isGravity) {
        baseParams.modelYear = modelYear;
      }

      const searchParams = new URLSearchParams(baseParams);

      // Add optional filter params if present
      if (filterParams.color) {
        searchParams.append('color', filterParams.color);
      }
      if (filterParams.interior) {
        searchParams.append('interior', filterParams.interior);
      }
      if (filterParams.wheels) {
        searchParams.append('wheels', filterParams.wheels);
      }
      if (filterParams.appearance) {
        searchParams.append('appearance', filterParams.appearance);
      }
      if (filterParams.driverAssistance) {
        searchParams.append('driverAssistance', filterParams.driverAssistance);
      }
      if (filterParams.soundSystem) {
        searchParams.append('soundSystem', filterParams.soundSystem);
      }
      if (filterParams.seatingOptions) {
        searchParams.append('seatingOptions', filterParams.seatingOptions);
      }
      if (filterParams.vehiclePackage) {
        searchParams.append('vehiclePackage', filterParams.vehiclePackage);
      }

      // Build the URL based on model selection
      const endpoint = isGravity ? '/vehicles/gravity' : '/vehicles';
      // Don't encode commas - the API expects raw commas in array params
      const queryString = searchParams.toString().replace(/%2C/g, ',');
      const inventoryBaseUrl = getInventoryBaseUrl(locale);
      const response = await fetch(`${inventoryBaseUrl}${endpoint}?${queryString}`, {
        method: 'GET',
        headers: {
          accept: 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch inventory vehicles: ${response.statusText}`);
      }

      return response.json();
    },
    enabled,
    placeholderData: keepPreviousData,
  });
};

// Re-export transformers for easy access
export {
  transformVehicle,
  transformVehicles,
  transformPrCodes,
  applyFilters,
  isOptionAvailableForTrims,
  getAvailabilityLabel,
  formatDistance,
  formatPrice,
  type TransformedVehicle,
  type ParsedFilterOptions,
} from './transformers.inventory';

/*
|--------------------------------------------------------------------------
| GET VEHICLE CHECKOUT DETAIL
|--------------------------------------------------------------------------
*/

/**
 * Fetches detailed checkout information for a specific vehicle
 * Returns transformed data ready for UI consumption
 * @param locale - The locale for the API call (default: 'en-us')
 * @returns React Query mutation for fetching checkout details
 *
 * @example
 * const { mutateAsync: getCheckoutDetail } = useGetCheckoutDetail('en-us');
 * const details = await getCheckoutDetail({
 *   shortCode: 'FLRXM3',
 *   batchNumber: '0000054253',
 *   uuid: '692f6a5ac9063ea5a09c5ba5',
 *   salesMode: false,
 *   type: 'new',
 *   zipState: 'CA',
 * });
 */
export const useGetCheckoutDetail = (locale = 'en-us') => {
  return useMutation({
    mutationKey: [LUPP_QUERY_KEYS.CHECKOUT_DETAIL, locale],
    mutationFn: async (params: CheckoutDetailRequest): Promise<TransformedCheckoutDetail> => {
      const inventoryBaseUrl = getInventoryBaseUrl(locale);
      const response = await fetch(`${inventoryBaseUrl}/vehicle/checkoutDetail`, {
        method: 'POST',
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
        },
        body: JSON.stringify(params),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch checkout detail: ${response.statusText}`);
      }

      const data: CheckoutDetailResponse = await response.json();
      return transformCheckoutDetail(data);
    },
  });
};
